import { memo, useMemo, type ReactNode } from 'react';
import { traceStep, traceWhat } from '../engine/localize';
import type { CompileResult, Row, RowSizeResult, TraceStep } from '../engine/types';
import { formatNumber, useI18n } from '../i18n';
import { cellStatus, cellTone, readByIsland, rowStatus, rowTone, toneNoteKey } from '../lib/rowStatus';
import { sizeName } from '../lib/sizes';
import { SeverityIcon } from '../ui/Icon';
import { RowLabel } from './RowLabel';
import { StatusLegend, StatusSummary, TONE_STYLE, ToneGlyph } from './RowStatus';

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

interface VisualProps {
  result: CompileResult;
  selectedSize: number | null;
  activeLine: number | null;
  onJump: (line: number) => void;
}

/**
 * Stitches, row by row: one line per pattern row, one bar per size proportional to the count
 * (the longest bar is the widest count, usually the cast-on), the count at the end of the bar.
 * Rows with an error are tinted; a click finds the row in the pattern.
 */
export const RowsVisual = memo(function RowsVisual({ result, selectedSize, activeLine, onJump }: VisualProps) {
  const { t, lang } = useI18n();
  const sizes = result.document.sizes;
  const max = useMemo(() => {
    let m = 0;
    for (const r of result.rows) for (const p of r.per_size) for (const v of [p.available, p.count_after]) if (num(v)) m = Math.max(m, v);
    return m;
  }, [result.rows]);
  const first = result.rows[0];
  const castOn = first?.per_size.some((p) => num(p.available)) ? first : null;

  return (
    <div data-testid="rows-visual">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="min-w-[14rem] flex-1 text-xs leading-relaxed text-muted">{t('rows.visualCaption', { max: formatNumber(lang, max) })}</p>
        <StatusSummary result={result} selectedSize={selectedSize} />
      </div>
      <ol className="space-y-1">
        {castOn && (
          <li className="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)] gap-4 px-3 py-1.5">
            <span className="kicker pt-0.5">{t('rows.castOn')}</span>
            <Bars
              values={sizes.map((s) => ({ size: s.index, ps: castOn.per_size.find((p) => p.size === s.index), value: castOn.per_size.find((p) => p.size === s.index)?.available ?? null }))}
              max={max}
              result={result}
              selectedSize={selectedSize}
            />
          </li>
        )}
        {result.rows.map((row) => {
          const error = row.per_size.some((p) => p.has_error);
          const warning = !error && row.per_size.some((p) => p.has_warning);
          const active = row.line_no === activeLine;
          return (
            <li key={`${row.line_no}-${row.label}`}>
              <button
                type="button"
                onClick={() => onJump(row.line_no)}
                data-testid="rows-visual-row"
                data-line={row.line_no}
                data-state={error ? 'error' : warning ? 'warning' : 'ok'}
                data-status={rowStatus(row, selectedSize) ?? undefined}
                data-tone={rowTone(row, selectedSize) ?? undefined}
                className={`grid w-full grid-cols-[minmax(0,8rem)_minmax(0,1fr)] gap-4 rounded-control px-3 py-1.5 text-left transition-colors ${
                  error ? 'bg-error/[0.06] hover:bg-error/[0.09]' : active ? 'bg-accent/[0.07]' : 'hover:bg-fg/[0.03]'
                }`}
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5">
                    {error ? <SeverityIcon severity="error" size={12} /> : warning ? <SeverityIcon severity="warning" size={12} /> : null}
                    <RowLabel lineNo={row.line_no} label={row.label} result={result} className="font-display truncate text-sm font-[520] text-fg" />
                  </span>
                  <span className="mt-0.5 block text-2xs text-subtle">{t('diag.line', { n: row.line_no })}</span>
                </span>
                <Bars
                  values={sizes.map((s) => {
                    const ps = row.per_size.find((p) => p.size === s.index);
                    return { size: s.index, ps, value: ps?.count_after ?? null };
                  })}
                  max={max}
                  result={result}
                  selectedSize={selectedSize}
                  row={row}
                />
              </button>
            </li>
          );
        })}
      </ol>
      <StatusLegend result={result} className="mt-4" />
    </div>
  );
});

/**
 * One bar per size. Error and warning bars keep their tones; otherwise the reading tone shapes the bar
 * (ADR-013, decision 6): a filled lichen bar for a verified count, a dotted honey outline with « ~ » for a count
 * taken on trust, a dashed honey outline with « ≈ » for a row read by island and not confirmed, an amber bar with
 * « ◇ » for an unread row with proposals, a faint grey bar with « ? » for a row that was not read. The cast-on has
 * no status.
 */
function Bars({
  values,
  max,
  result,
  selectedSize,
  row,
}: {
  values: Array<{ size: number; ps: RowSizeResult | undefined; value: number | null | undefined }>;
  max: number;
  result: CompileResult;
  selectedSize: number | null;
  row?: Row;
}) {
  const { t, lang } = useI18n();
  const many = values.length > 1;
  return (
    <span className="block min-w-0">
      {values.map(({ size, ps, value }) => {
        const name = sizeName(result, size, lang);
        const faded = selectedSize !== null && selectedSize !== size;
        const na = ps && !ps.applies;
        const state = na ? 'na' : ps?.has_error ? 'error' : ps?.has_warning ? 'warning' : 'ok';
        const status = row && !na ? cellStatus(ps) : null;
        const readingTone = row && !na ? cellTone(row, ps) : null;
        const noteKey = row ? toneNoteKey(readingTone, readByIsland(row)) : null;
        // The cast-on (no row) is a plain count: ink.
        const tone =
          state === 'error' ? 'bg-error' : state === 'warning' ? 'bg-warning' : readingTone ? TONE_STYLE[readingTone].bar : 'bg-fg/45';
        const ink =
          state === 'error'
            ? 'font-bold text-error'
            : state === 'warning'
              ? 'font-semibold text-warning'
              : readingTone
                ? TONE_STYLE[readingTone].ink
                : 'font-semibold text-fg';
        const width = num(value) && max > 0 ? Math.max(1.5, (value / max) * 100) : 0;
        const title = [num(value) ? t('rows.barTitle', { size: name, n: value }) : null, noteKey ? t(noteKey) : null].filter(Boolean).join(' · ');
        return (
          <span
            key={size}
            className={`grid items-center gap-2 ${many ? 'grid-cols-[3rem_minmax(0,1fr)]' : 'grid-cols-[minmax(0,1fr)]'} ${faded ? 'opacity-35' : ''}`}
            data-testid="count-bar"
            data-size={size}
            data-state={state}
            data-status={status ?? undefined}
            data-tone={readingTone ?? undefined}
            title={title || undefined}
          >
            {many && <span className="truncate text-2xs font-semibold text-muted">{name}</span>}
            {na ? (
              <span className="text-2xs italic text-subtle">— {t('rows.notApplicable')}</span>
            ) : (
              <BarTrack
                width={width}
                tone={tone}
                dim={Boolean(ps?.resynced)}
                glyph={<ToneGlyph tone={readingTone} />}
                label={num(value) ? formatNumber(lang, value) : status === 'unread' ? '' : '?'}
                labelClass={`${ink} ${ps?.resynced ? 'underline decoration-dotted decoration-2 underline-offset-[3px]' : ''}`}
              />
            )}
            {noteKey && <span className="sr-only"> ({t(noteKey)})</span>}
          </span>
        );
      })}
    </span>
  );
}

/** A bar whose count sits right at its end (the track keeps room for the count past 100 %). */
function BarTrack({
  width,
  tone,
  label,
  labelClass,
  dim = false,
  glyph = null,
}: {
  width: number;
  tone: string;
  label: string;
  labelClass: string;
  dim?: boolean;
  glyph?: ReactNode;
}) {
  return (
    <span className="relative mr-12 block h-[18px] min-w-0">
      {width > 0 && (
        <span
          className={`absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-pill ${tone} ${dim ? 'opacity-60' : ''}`}
          style={{ width: `${width}%` }}
          aria-hidden="true"
        />
      )}
      <span className={`absolute top-1/2 inline-flex -translate-y-1/2 items-center gap-1 whitespace-nowrap pl-2 text-xs tabular ${labelClass}`} style={{ left: `${width}%` }}>
        {glyph}
        {label}
      </span>
    </span>
  );
}

/** Steps verified by a module (shaping schedule, short-row heel): one bar per size, scaled within the step. */
export const TraceVisual = memo(function TraceVisual({
  result,
  trace,
  selectedSize,
  activeLine,
  onJump,
}: VisualProps & { trace: TraceStep[] }) {
  const { t, lang } = useI18n();
  const sizes = result.document.sizes;
  const errorLines = new Set(result.diagnostics.filter((d) => d.severity === 'error').map((d) => `${d.line_no}:${d.size}`));
  return (
    <div data-testid="trace-visual">
      <p className="mb-4 text-xs leading-relaxed text-muted">{t('rows.stepsCaption')}</p>
      <ol className="space-y-1">
        {trace.map((step, i) => {
          const nums = step.values.filter(num);
          const max = nums.length ? Math.max(...nums) : 0;
          const bad = (size: number) => errorLines.has(`${step.line_no}:${size}`) || errorLines.has(`${step.line_no}:null`);
          const error = sizes.some((s) => bad(s.index));
          return (
            <li key={`${step.line_no}-${i}`}>
              <button
                type="button"
                onClick={() => onJump(step.line_no)}
                data-testid="trace-visual-row"
                data-line={step.line_no}
                data-state={error ? 'error' : 'ok'}
                className={`grid w-full grid-cols-[minmax(0,11rem)_minmax(0,1fr)] gap-4 rounded-control px-3 py-2 text-left transition-colors ${
                  error ? 'bg-error/[0.06] hover:bg-error/[0.09]' : step.line_no === activeLine ? 'bg-accent/[0.07]' : 'hover:bg-fg/[0.03]'
                }`}
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5">
                    {error && <SeverityIcon severity="error" size={12} />}
                    <span className="font-display text-sm font-[520] text-fg">{traceStep(step, lang).text}</span>
                  </span>
                  <span className="mt-0.5 block text-2xs text-subtle">
                    {traceWhat(step, lang).text} · {t('diag.line', { n: step.line_no })}
                  </span>
                </span>
                <span className="block min-w-0 space-y-1">
                  {sizes.map((s) => {
                    const v = step.values[s.index];
                    const width = num(v) && max > 0 ? Math.max(1.5, (v / max) * 100) : 0;
                    const isBad = bad(s.index);
                    return (
                      <span
                        key={s.index}
                        className={`grid grid-cols-[3.5rem_minmax(0,1fr)] items-center gap-2 ${selectedSize !== null && selectedSize !== s.index ? 'opacity-35' : ''}`}
                        data-testid="count-bar"
                        data-state={isBad ? 'error' : 'ok'}
                      >
                        <span className="truncate text-2xs font-semibold text-muted">{sizeName(result, s.index, lang)}</span>
                        <BarTrack
                          width={width}
                          tone={isBad ? 'bg-error' : 'bg-fg/45'}
                          label={v === null || v === undefined ? '—' : num(v) ? formatNumber(lang, v) : String(v)}
                          labelClass={isBad ? 'font-bold text-error' : 'font-semibold text-fg'}
                        />
                      </span>
                    );
                  })}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
});
